import { memo, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Markup, { bodyClass } from "./boothmarkup";
import { usePage } from "../hooks/usePage";

import {
  getBooths,
  getBoothById,
  createBooth,
  updateBooth,
  clearBoothError,
} from "../store/slices/booth";

import { getSubDivisions } from "../store/slices/subdivision";
import { getCircles } from "../store/slices/circle";
import { getStations } from "../store/slices/station";
import { getOfficers } from "../store/slices/user";

const PageMarkup = memo(Markup);

const initialForm = {
  boothId: "",
  code: "",
  name: "",
  subDivisionId: "",
  circleId: "",
  policeStationId: "",
  lat: "",
  lng: "",
  sensitivity: "NORMAL",
  votersCount: 0,
  geofenceRadius: 75,
  active: true,
  assignedStaff: [],
};

const getId = (value) => {
  if (!value) return "";
  return typeof value === "object" ? value._id || "" : value;
};

const normalizeBooth = (data) => {
  if (!data) return null;
  return data.booth || data.data?.booth || data.data || data;
};

export default function Booths() {
  usePage({
    title: "All Polling Booths - Nellore Police Dashboard",
    bodyClass,
  });

  const dispatch = useDispatch();

  const {
    booths = [],
    loading,
    createLoading,
    updateLoading,
    error,
    detailsLoading,
  } = useSelector((state) => state.booth);

  const { subdivisions = [] } = useSelector(
    (state) => state.subdivision
  );

  const { circles = [] } = useSelector(
    (state) => state.circle
  );

  const { stations = [] } = useSelector(
    (state) => state.station
  );

  const { officers = [] } = useSelector(
    (state) => state.user
  );

  const [search, setSearch] = useState("");
  const [sensitivity, setSensitivity] = useState("ALL");
  const [status, setStatus] = useState("ALL");
  const [viewMode, setViewMode] = useState("grid");

  const [showModal, setShowModal] = useState(false);
  const [editingBoothId, setEditingBoothId] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [formError, setFormError] = useState("");

  // --------------------------------------------------
  // INITIAL DATA
  // --------------------------------------------------

  const loadData = () => {
    dispatch(getBooths({ page: 1, limit: 1000 }));
    dispatch(getSubDivisions({ page: 1, limit: 1000 }));
    dispatch(getCircles({ page: 1, limit: 1000 }));
    dispatch(getStations({ page: 1, limit: 1000 }));
    dispatch(
      getOfficers({
        active: true,
        page: 1,
        limit: 1000,
      })
    );
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // --------------------------------------------------
  // FILTER DATA
  // --------------------------------------------------

  const filteredBooths = useMemo(() => {
    return booths.filter((booth) => {
      const searchText = search.trim().toLowerCase();

      const matchesSearch =
        !searchText ||
        [
          booth.name,
          booth.boothId,
          booth.code,
          booth.policeStationName,
          booth.policeStationId?.name,
          booth.subDivisionName,
          booth.subDivisionId?.name,
          booth.circleId?.name,
        ].some((value) =>
          String(value || "").toLowerCase().includes(searchText)
        );

      const boothSensitivity = String(
        booth.sensitivity || "NORMAL"
      )
        .toUpperCase()
        .replaceAll(" ", "_");

      const matchesSensitivity =
        sensitivity === "ALL" ||
        boothSensitivity === sensitivity;

      const matchesStatus =
        status === "ALL" ||
        (status === "ACTIVE" && booth.active === true) ||
        (status === "INACTIVE" && booth.active === false);

      return (
        matchesSearch &&
        matchesSensitivity &&
        matchesStatus
      );
    });
  }, [booths, search, sensitivity, status]);

  // --------------------------------------------------
  // STATISTICS
  // --------------------------------------------------

  const statistics = useMemo(
    () => ({
      total: booths.length,

      hyperSensitive: booths.filter(
        (booth) =>
          String(booth.sensitivity).toUpperCase() ===
          "HYPER_SENSITIVE"
      ).length,

      sensitive: booths.filter(
        (booth) =>
          String(booth.sensitivity).toUpperCase() ===
          "SENSITIVE"
      ).length,

      normal: booths.filter(
        (booth) =>
          !["HYPER_SENSITIVE", "SENSITIVE"].includes(
            String(booth.sensitivity || "NORMAL").toUpperCase()
          )
      ).length,

      active: booths.filter(
        (booth) => booth.active === true
      ).length,
    }),
    [booths]
  );

  // --------------------------------------------------
  // MODAL
  // --------------------------------------------------

  const openAddModal = () => {
    dispatch(clearBoothError());

    setEditingBoothId(null);
    setForm(initialForm);
    setFormError("");
    setShowModal(true);
  };

  const openEditModal = async (booth) => {
    dispatch(clearBoothError());

    setEditingBoothId(booth._id);
    setForm(initialForm);
    setFormError("");
    setShowModal(true);

    try {
      // Load all dropdown data and booth details together.
      const [
        subdivisionData,
        circleData,
        stationData,
        officerData,
        boothData,
      ] = await Promise.all([
        dispatch(
          getSubDivisions({ page: 1, limit: 1000 })
        ).unwrap(),

        dispatch(
          getCircles({ page: 1, limit: 1000 })
        ).unwrap(),

        dispatch(
          getStations({ page: 1, limit: 1000 })
        ).unwrap(),

        dispatch(
          getOfficers({ active: true, page: 1, limit: 1000 })
        ).unwrap(),

        dispatch(getBoothById(booth._id)).unwrap(),
      ]);

      // Normalize API response structures.
      const subdivisionList = Array.isArray(subdivisionData)
        ? subdivisionData
        : subdivisionData?.subDivisions || [];

      const circleList = Array.isArray(circleData)
        ? circleData
        : circleData?.circles || [];

      const stationList = Array.isArray(stationData)
        ? stationData
        : stationData?.stations || [];

      const officerList = Array.isArray(officerData)
        ? officerData
        : officerData?.officers || [];

      // The Redux slices already store these lists.
      // This also helps identify missing API data.
      if (!subdivisionList.length) {
        console.warn("No Sub-Divisions returned by API");
      }

      if (!circleList.length) {
        console.warn("No Circles returned by API");
      }

      if (!stationList.length) {
        console.warn("No Police Stations returned by API");
      }

      if (!officerList.length) {
        console.warn("No Officers returned by API");
      }

      const selectedBooth =
        boothData?.booth ||
        boothData?.data?.booth ||
        boothData?.data ||
        boothData;

      if (!selectedBooth?._id) {
        throw new Error("Booth details not found");
      }

      const assignedStaff = Array.isArray(
        selectedBooth.assignedStaff
      )
        ? selectedBooth.assignedStaff
          .filter(
            (staff) =>
              staff.assignmentStatus !== "UNASSIGNED"
          )
          .map((staff) => {
            const officer =
              typeof staff.officerId === "object"
                ? staff.officerId
                : null;

            return {
              officerId:
                officer?._id ||
                staff.officerId ||
                staff.officer?._id ||
                "",

              dutyWing:
                staff.dutyWing ||
                officer?.dutyWing ||
                "POLLING_STATION",

              generalNumber: staff.generalNumber || "",

              // Keep officer details even if the officer
              // is missing from the current officer list.
              officerDetails: officer || staff.officer || {
                _id: staff.officerId,
                displayName: staff.name,
                officerId: staff.officerCode,
                designation: staff.designation,
                phone: staff.phone,
              },
            };
          })
          .filter((staff) => staff.officerId)
        : [];

      setForm({
        boothId: selectedBooth.boothId || "",
        code: selectedBooth.code || "",
        name: selectedBooth.name || "",

        subDivisionId:
          selectedBooth.subDivisionId?._id ||
          selectedBooth.subDivisionId ||
          "",

        circleId:
          selectedBooth.circleId?._id ||
          selectedBooth.circleId ||
          "",

        policeStationId:
          selectedBooth.policeStationId?._id ||
          selectedBooth.policeStationId ||
          "",

        lat: selectedBooth.lat ?? "",
        lng: selectedBooth.lng ?? "",

        sensitivity: selectedBooth.sensitivity || "NORMAL",
        votersCount: selectedBooth.votersCount ?? 0,
        geofenceRadius: selectedBooth.geofenceRadius ?? 75,
        active: selectedBooth.active ?? true,

        assignedStaff,
      });

    } catch (err) {
      console.error("Edit booth loading error:", err);

      setFormError(
        typeof err === "string"
          ? err
          : err?.message || "Failed to load booth details"
      );
    }
  };

  const closeModal = () => {
    if (createLoading || updateLoading) return;

    setShowModal(false);
    setEditingBoothId(null);
    setForm(initialForm);
    setFormError("");
  };

  // --------------------------------------------------
  // FORM HANDLERS
  // --------------------------------------------------

  const handleFormChange = (field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const handleSubdivisionChange = (subDivisionId) => {
    setForm((previous) => ({
      ...previous,
      subDivisionId,
      circleId: "",
      policeStationId: "",
    }));
  };

  const handleCircleChange = (circleId) => {
    setForm((previous) => ({
      ...previous,
      circleId,
      policeStationId: "",
    }));
  };

  const handleOfficerToggle = (officerId, checked) => {
    setForm((previous) => {
      const existing = previous.assignedStaff || [];

      if (checked) {
        if (
          existing.some(
            (item) => item.officerId === officerId
          )
        ) {
          return previous;
        }

        return {
          ...previous,
          assignedStaff: [
            ...existing,
            {
              officerId,
              dutyWing: "POLLING_STATION",
              generalNumber: "",
            },
          ],
        };
      }

      return {
        ...previous,
        assignedStaff: existing.filter(
          (item) => item.officerId !== officerId
        ),
      };
    });
  };

  const handleOfficerDutyChange = (
    officerId,
    dutyWing
  ) => {
    setForm((previous) => ({
      ...previous,
      assignedStaff: previous.assignedStaff.map(
        (staff) =>
          staff.officerId === officerId
            ? { ...staff, dutyWing }
            : staff
      ),
    }));
  };

  const handleOfficerGeneralNumberChange = (
    officerId,
    generalNumber
  ) => {
    setForm((previous) => ({
      ...previous,
      assignedStaff: previous.assignedStaff.map(
        (staff) =>
          staff.officerId === officerId
            ? { ...staff, generalNumber }
            : staff
      ),
    }));
  };

  // --------------------------------------------------
  // CREATE / UPDATE
  // --------------------------------------------------

  const handleSubmit = async (event) => {
    event?.preventDefault();

    setFormError("");

    if (
      !form.boothId.trim() ||
      !form.code.trim() ||
      !form.name.trim() ||
      !form.subDivisionId ||
      !form.circleId ||
      !form.policeStationId ||
      form.lat === "" ||
      form.lng === ""
    ) {
      setFormError("Please fill all required fields");
      return;
    }

    const lat = Number(form.lat);
    const lng = Number(form.lng);

    if (
      !Number.isFinite(lat) ||
      !Number.isFinite(lng) ||
      lat < -90 ||
      lat > 90 ||
      lng < -180 ||
      lng > 180
    ) {
      setFormError("Please enter valid GPS coordinates");
      return;
    }

    const payload = {
      boothId: form.boothId.trim(),
      code: form.code.trim(),
      name: form.name.trim(),

      subDivisionId: form.subDivisionId,
      circleId: form.circleId,
      policeStationId: form.policeStationId,

      lat,
      lng,

      sensitivity: form.sensitivity,
      votersCount: Number(form.votersCount) || 0,
      geofenceRadius: Number(form.geofenceRadius) || 75,

      active: form.active,

      assignedStaff: form.assignedStaff.map(
        (staff) => ({
          officerId: staff.officerId,
          dutyWing: staff.dutyWing,
          generalNumber: staff.generalNumber || null,
        })
      ),
    };

    try {
      if (editingBoothId) {
        await dispatch(
          updateBooth({
            id: editingBoothId,
            payload,
          })
        ).unwrap();
      } else {
        await dispatch(createBooth(payload)).unwrap();
      }

      setShowModal(false);
      setEditingBoothId(null);
      setForm(initialForm);
      setFormError("");

      dispatch(getBooths({ page: 1, limit: 1000 }));
    } catch (err) {
      setFormError(
        typeof err === "string"
          ? err
          : err?.message || "Failed to save booth"
      );
    }
  };

  // --------------------------------------------------
  // RENDER
  // --------------------------------------------------

  return (
    <PageMarkup
      booths={filteredBooths}
      totalBooths={booths.length}
      statistics={statistics}
      loading={loading}
      detailsLoading={detailsLoading}
      createLoading={createLoading}
      updateLoading={updateLoading}
      error={error}
      search={search}
      setSearch={setSearch}
      sensitivity={sensitivity}
      setSensitivity={setSensitivity}
      status={status}
      setStatus={setStatus}
      viewMode={viewMode}
      setViewMode={setViewMode}
      showModal={showModal}
      editingBoothId={editingBoothId}
      form={form}
      formError={formError}
      subdivisions={subdivisions}
      circles={circles}
      stations={stations}
      officers={officers}
      onAddBooth={openAddModal}
      onEditBooth={openEditModal}
      onCloseModal={closeModal}
      onFormChange={handleFormChange}
      onSubdivisionChange={handleSubdivisionChange}
      onCircleChange={handleCircleChange}
      onOfficerToggle={handleOfficerToggle}
      onOfficerDutyChange={handleOfficerDutyChange}
      onOfficerGeneralNumberChange={
        handleOfficerGeneralNumberChange
      }
      onSubmit={handleSubmit}
    />
  );
}